import { startTransition, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { getSocket } from "@/hooks/useSocketStatus";
import type { Socket } from "socket.io-client";

const API_URL = import.meta.env.VITE_API_URL || "";

// Phase 20.3 — backoff schedule for EventSource reconnect after the
// server drops the stream (pm2 reload, network hiccup, etc.). Capped at
// 30 s so we don't hammer the server during a long outage but recover
// within seconds once it's back.
const SSE_BACKOFF_MS = [1000, 2000, 5000, 10_000, 30_000];

interface LiveEvent {
  id: string;
  type: "device_enrolled" | "heartbeat" | "screenshot" | "connected";
  timestamp: Date;
  message: string;
  hostname?: string;
  url?: string;
}

interface FeedState {
  events: LiveEvent[];
  connected: boolean;
  terminalTexts: Record<string, string>;
}

// ──────────────────────────────────────────────────────────────────────
// Phase 2 (foundation) — shared live-feed store.
//
// Previously every component that called useLiveFeed() opened its OWN
// `io()` socket *and* its own EventSource. Two consequences:
//   1. The dashboard mounts 4+ of these at once (ActivityFeed,
//      LiveDeviceStatus, HeaderSocketPill, RealtimeNotificationsProvider)
//      → 4+ parallel sockets.
//   2. That `io()` call never passed an auth token, so the server dropped
//      each socket and socket.io retried on a 1-30 s loop — the visible
//      "connect/disconnect every 2-6 s" storm.
//
// Fix: a single module-level store, refcounted across all consumers, that
// reuses the *authenticated* singleton socket owned by useSocketStatus
// (getSocket()) and opens exactly one EventSource. Each hook instance just
// subscribes to the shared snapshot — no socket of its own. We attach our
// own event handlers to the singleton and remove them on teardown, but we
// NEVER disconnect it: useSocketStatus owns that lifecycle.
// ──────────────────────────────────────────────────────────────────────

let _state: FeedState = { events: [], connected: false, terminalTexts: {} };
let _listeners: Array<(s: FeedState) => void> = [];
let _refcount = 0;

let _socket: Socket | null = null;
let _handlers: Record<string, (...args: any[]) => void> | null = null;

let _es: EventSource | null = null;
let _reconnectTimer: number | null = null;
let _attempt = 0;
let _cancelled = false;

function _emit() {
  for (const l of _listeners) {
    try { l(_state); } catch { /* a dead subscriber must not break the rest */ }
  }
}

function _set(patch: Partial<FeedState>) {
  _state = { ..._state, ...patch };
  _emit();
}

function _pushEvent(ev: LiveEvent) {
  _state = { ..._state, events: [ev, ..._state.events].slice(0, 50) };
  _emit();
}

function _openStream(token: string) {
  if (_cancelled) return;
  const es = new EventSource(`${API_URL}/api/events/stream?token=${token}`);
  _es = es;
  es.onopen = () => { _attempt = 0; };
  es.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      if (data.type === "connected") return;
      if (data.type === "heartbeat" && data.device?.terminalText && data.device?.hostname) {
        _set({ terminalTexts: { ..._state.terminalTexts, [data.device.hostname]: data.device.terminalText } });
      }
    } catch { /* tolerate parse errors */ }
  };
  es.onerror = () => {
    try { es.close(); } catch { /* ignore */ }
    if (_cancelled) return;
    const i = Math.min(_attempt++, SSE_BACKOFF_MS.length - 1);
    _reconnectTimer = window.setTimeout(() => _openStream(token), SSE_BACKOFF_MS[i]);
  };
}

function _startFeed(orgId: string, token: string) {
  _refcount++;
  if (_refcount > 1) return; // already running — just share it
  _cancelled = false;

  const socket = getSocket(); // authenticated singleton, owned by useSocketStatus
  _socket = socket;

  const joinOrg = () => { if (orgId) socket.emit("join-org", orgId); };

  const onConnect = () => { _set({ connected: true }); joinOrg(); };
  const onDisconnect = () => _set({ connected: false });

  const onHeartbeat = (data: any) => {
    if (data.device?.terminalText && data.device?.hostname) {
      _set({ terminalTexts: { ..._state.terminalTexts, [data.device.hostname]: data.device.terminalText } });
    }
    _pushEvent({
      id: Math.random().toString(36).slice(2),
      type: "heartbeat",
      timestamp: new Date(),
      hostname: data.device?.hostname || "",
      message: `${data.device?.hostname} is ${data.device?.status}`,
    });
  };

  const onScreenshot = (data: any) => {
    _pushEvent({
      id: Math.random().toString(36).slice(2),
      type: "screenshot",
      timestamp: new Date(),
      hostname: data.screenshot?.hostname || "",
      message: `Screenshot from ${data.screenshot?.hostname}`,
      url: data.screenshot?.url,
    });
  };

  const onEnrolled = (data: any) => {
    _pushEvent({
      id: Math.random().toString(36).slice(2),
      type: "device_enrolled",
      timestamp: new Date(),
      hostname: data.device?.hostname || "",
      message: `New device enrolled: ${data.device?.hostname}`,
    });
  };

  socket.on("connect", onConnect);
  socket.on("disconnect", onDisconnect);
  socket.on("heartbeat", onHeartbeat);
  socket.on("screenshot", onScreenshot);
  socket.on("device_enrolled", onEnrolled);
  _handlers = { connect: onConnect, disconnect: onDisconnect, heartbeat: onHeartbeat, screenshot: onScreenshot, device_enrolled: onEnrolled };

  // The singleton may already be connected (useSocketStatus mounts first
  // via HeaderSocketPill). If so, 'connect' has already fired — sync the
  // state and join the org room now rather than waiting for a reconnect.
  if (socket.connected) { _set({ connected: true }); joinOrg(); }

  _openStream(token);
}

function _stopFeed() {
  _refcount = Math.max(0, _refcount - 1);
  if (_refcount > 0) return; // other consumers still active

  _cancelled = true;
  if (_reconnectTimer != null) { window.clearTimeout(_reconnectTimer); _reconnectTimer = null; }

  if (_socket && _handlers) {
    for (const [evt, fn] of Object.entries(_handlers)) _socket.off(evt, fn);
  }
  _handlers = null;
  // NB: do NOT _socket.disconnect() — useSocketStatus owns the singleton.
  _socket = null;

  try { _es?.close(); } catch { /* ignore */ }
  _es = null;
}

export function useLiveFeed() {
  const { user } = useAuth();
  // Stabilize on primitives, not the user object: a parent re-render that
  // hands back a new `user` reference must NOT tear down and re-create the
  // feed. Only a real identity/org change should.
  const userId = user?.id || "";
  const orgId = (user as any)?.orgId || (user as any)?.org_id || "";

  const [snapshot, setSnapshot] = useState<FeedState>(_state);

  useEffect(() => {
    if (!userId) return;
    const token = localStorage.getItem("dk_token");
    if (!token) return;

    // Phase 20.3 — non-urgent realtime updates flow through startTransition
    // so a server reload mid-render can't trip React 426.
    const sub = (s: FeedState) => startTransition(() => setSnapshot(s));
    _listeners.push(sub);
    setSnapshot(_state); // adopt current shared state immediately

    _startFeed(orgId, token);

    return () => {
      _listeners = _listeners.filter(l => l !== sub);
      _stopFeed();
    };
  }, [userId, orgId]);

  return { events: snapshot.events, connected: snapshot.connected, terminalTexts: snapshot.terminalTexts };
}
